import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, CheckCircle2, AlertCircle, Hash, 
  ArrowRight, Calendar, ArrowRightCircle, FileText 
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const countryFlags = {
  CO: '🇨🇴',
  VE: '🇻🇪',
  US: '🇺🇸',
  PE: '🇵🇪',
  PA: '🇵🇦',
  EC: '🇪🇨',
};

// 🔥 MAPEO DE PASOS → RUTAS REALES
const stepRoutes = {
  select_countries: () => "/send/step1",
  destination: () => "/send/step2",
  amount: () => "/send/step3",
  payment_origin: () => "/send/step4",
  payment_destination: () => "/send/step5",
  confirm: () => "/send/step5",
};

// 🔥 NOMBRES BONITOS PARA BOTÓN GRIS
const stepNames = {
  select_countries: "Elegir países",
  destination: "Método de pago",
  amount: "Método de recepción",
  payment_origin: "Resumen del Envío",
  payment_destination: "Confirmar envío",
  confirm: "Confirmar envío",
};

const History = () => {
  const { transactions, user, fetchTransactions } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    fetchTransactions(user.id);
  }, [user]);

  const getCountryFlag = (countryCode) => countryFlags[countryCode?.toUpperCase()] || "🌍";

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'completado':
        return {
          bg: 'bg-green-50',
          text: 'text-green-800',
          border: 'border-green-200',
          icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
        };
      case 'procesando':
        return {
          bg: 'bg-yellow-50',
          text: 'text-yellow-800',
          border: 'border-yellow-200',
          icon: <Clock className="w-5 h-5 text-yellow-600" />,
        };
      case 'cancelada':
        return {
          bg: 'bg-red-50',
          text: 'text-red-800',
          border: 'border-red-200',
          icon: <AlertCircle className="w-5 h-5 text-red-600" />,
        };
      default:
        return {
          bg: 'bg-slate-50',
          text: 'text-slate-700',
          border: 'border-slate-200',
          icon: <Clock className="w-5 h-5 text-slate-400" />,
        };
    }
  };

  const handleStatusClick = (tx) => {
    const status = tx.status?.toLowerCase();

    if (status === 'completado' || status === 'procesando') {
      navigate(`/resumen-final`, { state: { transactionId: tx.id } });
      return;
    }

    if (status === 'cancelada') {
      window.open(`https://wa.me/573241437215`, '_blank');
      return;
    }
  };

  const handleGrayButtonClick = (tx) => {
    const step = tx.current_step;
    if (step && stepRoutes[step]) {
      navigate(stepRoutes[step](), { state: { transactionId: tx.id } });
    } else {
      navigate(`/enviar-dinero/${tx.id}`, { state: { transactionId: tx.id } });
    }
  };

  const normalizeTransaction = (tx) => ({
    ...tx,
    amount: tx.amount ?? 0,
    received_amount: tx.received_amount ?? 0,
    origin_country: tx.origin_country ?? '',
    destination_country: tx.destination_country ?? '',
    created_at: tx.created_at ?? new Date().toISOString(),
    status: tx.status ?? 'pendiente',
    destination_reference_number: tx.destination_reference_number ?? '',
    admin_reference_number: tx.admin_reference_number ?? '',
    current_step: tx.current_step ?? null,
  });

  // === ESTADO VACÍO ===
  if (!transactions || transactions.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#f4f7fb] flex flex-col items-center justify-center px-5 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-sm w-full bg-white p-8 rounded-[32px] shadow-sm border border-slate-100"
          >
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-blue-300" />
            </div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight mb-3">Sin envíos aún</h1>
            <p className="text-slate-500 font-medium leading-relaxed mb-6">
              Cuando realices tu primer envío de dinero, aparecerá aquí tu historial completo.
            </p>
            <button 
              onClick={() => navigate('/send/step1')}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl transition-all shadow-lg shadow-blue-600/20"
            >
              Enviar dinero ahora
            </button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // === HISTORIAL CON DATOS ===
  return (
    <Layout>
      <div className="min-h-screen bg-[#f4f7fb] px-4 py-8 md:py-12 pb-32">
        <div className="mx-auto w-full max-w-2xl">
          
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 text-center"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-600 rounded-full mb-4 shadow-sm">
              <FileText className="w-6 h-6" />
            </div>
            <h1 className="text-2xl md:text-[32px] font-black text-slate-800 tracking-tight leading-tight mb-1">
              Historial de envíos
            </h1>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">
              {transactions.length} {transactions.length === 1 ? 'operación registrada' : 'operaciones registradas'}
            </p>
          </motion.div>

          <div className="space-y-5">
            {transactions.map((transaction, index) => {
              const tx = normalizeTransaction(transaction);
              const status = getStatusStyles(tx.status);
              const statusLower = tx.status?.toLowerCase();

              const showColoredButton = ['procesando', 'completado', 'cancelada'].includes(statusLower);
              const showGrayButton = !showColoredButton;
              const grayButtonLabel = tx.current_step ? (stepNames[tx.current_step] || "Continuar envío") : "Continuar envío";

              return (
                <motion.div
                  key={tx.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="p-5 md:p-6">
                    
                    {/* FECHA Y HORA */}
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">
                      <Calendar className="w-3.5 h-3.5" />
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                    </div>

                    {/* TARJETA INTERNA DE FLUJO DE DINERO */}
                    <div className="flex items-center justify-between bg-slate-50 rounded-[24px] p-4 md:p-5 mb-5 border border-slate-100/50">
                      {/* Origen */}
                      <div className="flex flex-col">
                        <span className="text-2xl mb-1">{getCountryFlag(tx.origin_country)}</span>
                        <span className="font-black text-slate-800 text-lg md:text-xl tracking-tight">
                          {tx.amount.toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Flecha */}
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100">
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-slate-300" />
                      </div>
                      
                      {/* Destino */}
                      <div className="flex flex-col items-end">
                        <span className="text-2xl mb-1">{getCountryFlag(tx.destination_country)}</span>
                        <span className="font-black text-green-500 text-lg md:text-xl tracking-tight">
                          {tx.received_amount.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* BOTONES DE ESTADO Y ACCIÓN */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      {/* BOTÓN COLOR (Completado / Procesando / Cancelado) */}
                      {showColoredButton && (
                        <button
                          onClick={() => handleStatusClick(tx)}
                          className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl border transition-all active:scale-[0.98] ${status.bg} ${status.border} ${status.text} hover:opacity-80`}
                        >
                          {status.icon}
                          <span className="font-black uppercase tracking-widest text-xs">{tx.status}</span>
                        </button>
                      )}

                      {/* BOTÓN GRIS (Pendiente / Continuar) */}
                      {showGrayButton && (
                        <button
                          onClick={() => handleGrayButtonClick(tx)}
                          className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-black uppercase tracking-widest text-xs transition-all active:scale-[0.98]"
                        >
                          {grayButtonLabel} <ArrowRightCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* REFERENCIA DE DESTINO (Si existe) */}
                    {tx.destination_reference_number && (
                      <div className="mt-4 bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-sm">
                          <Hash className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-blue-400">Ref. de destino</p>
                          <p className="text-sm font-black text-blue-900 font-mono tracking-wider">
                            {tx.destination_reference_number}
                          </p>
                        </div>
                      </div>
                    )}

                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default History;

